// Animated hero carousel for the home page — cycles through the
// simplified geoVINUM product family with image + "for whom · why · CTA".
// Auto-advances every 5s, pauses on hover, mobile-friendly.
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Film, Frame, ShoppingBag, FileText, Sparkles } from "lucide-react";
import DownloadCatalogButton from "@/components/DownloadCatalogButton";
import HeroLiveIframe from "@/components/HeroLiveIframe";
import { useT, useLang } from "@/lib/i18n";

// Each slide has inline copy (FR primary) + a real marketing visual.
// Order reflects the new pillar narrative: Promise → App AI → Academy in-app
// → Maps → Iframe B2B → Blog.
const IMG_CDN = "https://customer-assets.emergentagent.com/job_geo-vinum-preview/artifacts";
const APP_CDN = "https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts";

// Localised copy for the 6 slides. Keeps translations co-located with the
// component so we don't have to edit the massive i18n.jsx dict for a hero
// tweak. Only 4 official geoVINUM languages are provided; others fall back to FR.
const SLIDES_COPY = {
  fr: {
    promise: { eye: "geoVINUM · l\u2019app", title: "Comprendre le vin. Visuellement. Intelligemment.", for: "Amateurs, sommeliers, professionnels du vin", why: "Une expérience digitale pour explorer, apprendre, déguster et comprendre.", cta: "Découvrir l\u2019app" },
    ai:      { eye: "geoVINUM app", title: "L\u2019IA qui vous accompagne avant, pendant et après la dégustation.", for: "Curieux qui veulent des réponses claires", why: "Explorer, déguster, apprendre et converser — dans une seule app.", cta: "Poser une question" },
    academy: { eye: "geoVINUM Academy · dans l\u2019app", title: "Apprendre le vin quelques minutes par jour.", for: "Étudiants sommellerie, passionnés", why: "Cours, révisions, flashcards, passeport de progression.", cta: "Ouvrir l\u2019Academy" },
    maps:    { eye: "geoVINUM Maps", title: "Comprendre le vin en le voyant.", for: "Amateurs, cavistes, écoles du vin", why: "Régions viticoles, appellations et terroirs — en physique et en numérique.", cta: "Voir la collection" },
    iframe:  { eye: "geoVINUM iframe · pros", title: "Ajoutez l\u2019intelligence géographique du vin sur votre site.", for: "Producteurs, interprofessions, plateformes vin", why: "Reliez un champ appellation à une carte interactive — automatiquement.", cta: "Voir les offres pros" },
    blog:    { eye: "Explorer", title: "Découvrir chaque appellation, cépage et terroir.", for: "Curieux qui veulent lire avant d\u2019acheter", why: "Articles enrichis, fiches Atlas publiques, cartes intégrées.", cta: "Ouvrir le blog" },
  },
  en: {
    promise: { eye: "geoVINUM · the app", title: "Understand wine. Visually. Intelligently.", for: "Wine lovers, sommeliers, wine professionals", why: "A digital experience to explore, learn, taste and understand.", cta: "Discover the app" },
    ai:      { eye: "geoVINUM app", title: "The AI that guides you before, during and after tasting.", for: "Curious minds who want clear answers", why: "Explore, taste, learn and chat — all in one app.", cta: "Ask a question" },
    academy: { eye: "geoVINUM Academy · in the app", title: "Learn wine, a few minutes a day.", for: "Sommelier students & passionate learners", why: "Courses, spaced revision, flashcards, progression passport.", cta: "Open the Academy" },
    maps:    { eye: "geoVINUM Maps", title: "Understand wine by seeing it.", for: "Wine lovers, wine shops, wine schools", why: "Wine regions, appellations and terroirs — physical and digital.", cta: "See the collection" },
    iframe:  { eye: "geoVINUM iframe · for pros", title: "Add the geographic intelligence of wine to your site.", for: "Producers, interprofessions, wine platforms", why: "Link an appellation field to an interactive map — automatically.", cta: "See pro offers" },
    blog:    { eye: "Explore", title: "Discover every appellation, grape and terroir.", for: "Readers who like to know before they buy", why: "Enriched articles, public Atlas fiches, embedded maps.", cta: "Open the blog" },
  },
  es: {
    promise: { eye: "geoVINUM · la app", title: "Comprender el vino. Visualmente. Inteligentemente.", for: "Amantes del vino, sumilleres, profesionales", why: "Una experiencia digital para explorar, aprender, catar y comprender.", cta: "Descubrir la app" },
    ai:      { eye: "geoVINUM app", title: "La IA que te acompaña antes, durante y después de la cata.", for: "Curiosos que buscan respuestas claras", why: "Explorar, catar, aprender y conversar — en una sola app.", cta: "Hacer una pregunta" },
    academy: { eye: "geoVINUM Academy · en la app", title: "Aprender de vino, unos minutos al día.", for: "Estudiantes de sumillería, apasionados", why: "Cursos, repaso, flashcards, pasaporte de progreso.", cta: "Abrir la Academy" },
    maps:    { eye: "geoVINUM Maps", title: "Comprender el vino viéndolo.", for: "Amantes del vino, tiendas, escuelas", why: "Regiones vitivinícolas, denominaciones y terruños — físico y digital.", cta: "Ver la colección" },
    iframe:  { eye: "geoVINUM iframe · pros", title: "Añade la inteligencia geográfica del vino a tu sitio.", for: "Productores, interprofesiones, plataformas", why: "Vincula un campo de denominación a un mapa interactivo — automático.", cta: "Ver ofertas pro" },
    blog:    { eye: "Explorar", title: "Descubrir cada denominación, uva y terruño.", for: "Lectores que quieren saber antes de comprar", why: "Artículos enriquecidos, fichas Atlas públicas, mapas integrados.", cta: "Abrir el blog" },
  },
  pt: {
    promise: { eye: "geoVINUM · a app", title: "Compreender o vinho. Visualmente. Inteligentemente.", for: "Amantes de vinho, sommeliers, profissionais", why: "Uma experiência digital para explorar, aprender, degustar e compreender.", cta: "Descobrir a app" },
    ai:      { eye: "geoVINUM app", title: "A IA que o acompanha antes, durante e depois da prova.", for: "Curiosos à procura de respostas claras", why: "Explorar, provar, aprender e conversar — numa só app.", cta: "Fazer uma pergunta" },
    academy: { eye: "geoVINUM Academy · na app", title: "Aprender vinho, alguns minutos por dia.", for: "Estudantes de sommellerie, apaixonados", why: "Cursos, revisão, flashcards, passaporte de progresso.", cta: "Abrir a Academy" },
    maps:    { eye: "geoVINUM Maps", title: "Compreender o vinho vendo-o.", for: "Amantes de vinho, garrafeiras, escolas", why: "Regiões vinícolas, denominações e terroirs — físico e digital.", cta: "Ver a coleção" },
    iframe:  { eye: "geoVINUM iframe · pros", title: "Adicione a inteligência geográfica do vinho ao seu site.", for: "Produtores, interprofissões, plataformas", why: "Ligue um campo de denominação a um mapa interativo — automaticamente.", cta: "Ver ofertas pro" },
    blog:    { eye: "Explorar", title: "Descobrir cada denominação, casta e terroir.", for: "Leitores que gostam de saber antes de comprar", why: "Artigos enriquecidos, fichas Atlas públicas, mapas integrados.", cta: "Abrir o blog" },
  },
};

const SLIDES = [
  { slug: "promise", Icon: Sparkles, ctaTo: "/geovinum-ai", isPromise: true, device: "live-iframe" },
  { slug: "ai",      Icon: Sparkles, image: `${APP_CDN}/urmu0z3s_geovinum-app-plusieurs-fac%CC%A7ons-dapprendre.png`, ctaTo: "/geovinum-ai", device: "phone" },
  { slug: "academy", Icon: Film,     image: `${APP_CDN}/om0ncw5p_geovinum-app-academy.png`, ctaTo: "/academy", device: "phone" },
  { slug: "maps",    Icon: ShoppingBag, image: `${APP_CDN}/5nly8uu9_geovinum-app-explorer-appellation.png`, ctaTo: "/shop", device: "phone" },
  { slug: "iframe",  Icon: Frame,    image: `${IMG_CDN}/3m7qbj61_geovinumio-iframe.jpg`, ctaTo: "/pro", device: "browser" },
  { slug: "blog",    Icon: FileText, image: `${IMG_CDN}/w94nxeyl_geovinumio-educativos.jpg`, ctaTo: "/blog", device: "browser" },
];

const AUTO_MS = 5000;

const AnimatedHero = () => {
  const t = useT();
  const lang = useLang();
  const copy = SLIDES_COPY[lang] || SLIDES_COPY.fr;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const tidRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    tidRef.current = setTimeout(() => setIdx((i) => (i + 1) % SLIDES.length), AUTO_MS);
    return () => clearTimeout(tidRef.current);
  }, [idx, paused]);

  return (
    <section
      className="pt-28 md:pt-36 pb-16 md:pb-24 relative overflow-hidden"
      data-testid="home-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-[color:var(--gv-mist)] to-white" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* LEFT — Text side, fades + slides on slide change */}
        <div className="lg:col-span-7 relative min-h-[420px]">
          {SLIDES.map((s, i) => {
            const active = i === idx;
            const { Icon } = s;
            const c = copy[s.slug] || SLIDES_COPY.fr[s.slug];
            return (
              <div
                key={s.slug}
                aria-hidden={!active}
                className={`absolute inset-0 transition-all duration-700 ease-out ${
                  active ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"
                }`}
                data-testid={`hero-text-${s.slug}`}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-5">
                  <Icon size={12} /> {c.eye}
                  {s.soon && <span className="text-[10px] tracking-widest text-[color:var(--gv-ink)]/55 border-l border-[color:var(--gv-line)] pl-2 ml-1">{t("hero.soon")}</span>}
                </div>
                <h1 className="font-display text-[40px] md:text-[60px] lg:text-[68px] leading-[1.04] text-[color:var(--gv-ink)] mb-5">
                  {c.title}
                </h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7 max-w-2xl">
                  <Pill label={t("hero.label.for")} value={c.for} />
                  <Pill label={t("hero.label.why")} value={c.why} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={s.ctaTo} className="btn-primary" data-testid={`hero-cta-${s.slug}`}>
                    {c.cta} <ArrowRight size={16} />
                  </Link>
                  <Link to="/shop" className="btn-secondary">
                    {t("hero.see_all")}
                  </Link>
                  <DownloadCatalogButton variant="ghost" testId="hero-catalog-download" />
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT — Visual side, framed inside a device mockup so app
            screenshots (portrait) sit inside an iPhone and web
            screenshots (landscape) sit inside a browser window. Prevents
            the previous zoom/crop artefact from `object-cover`. */}
        <div className="lg:col-span-5">
          <div className="relative flex items-center justify-center" data-testid="hero-visual">
            {SLIDES.map((s, i) => {
              const sc = copy[s.slug] || SLIDES_COPY.fr[s.slug] || {};
              return (
              <div
                key={s.slug}
                className={`w-full transition-opacity duration-700 ${
                  i === idx ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"
                }`}
                aria-hidden={i !== idx}
              >
                {s.device === "live-iframe" ? (
                  <HeroLiveIframe testId={`hero-image-${s.slug}`} />
                ) : s.device === "phone" ? (
                  <PhoneMockup src={s.image} alt={sc.title} testId={`hero-image-${s.slug}`} eye={sc.eye} idx={idx} total={SLIDES.length} />
                ) : (
                  <BrowserMockup src={s.image} alt={sc.title} testId={`hero-image-${s.slug}`} eye={sc.eye} idx={idx} total={SLIDES.length} />
                )}
              </div>
              );
            })}
          </div>

          {/* Progress dots */}
          <div className="mt-5 flex items-center justify-center gap-2" data-testid="hero-dots">
            {SLIDES.map((s, i) => (
              <button
                key={s.slug}
                onClick={() => setIdx(i)}
                aria-label={t(s.eyeKey)}
                data-testid={`hero-dot-${s.slug}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === idx ? "w-8 bg-[color:var(--gv-pink)]" : "w-1.5 bg-[color:var(--gv-line)] hover:bg-[color:var(--gv-ink)]/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom strip — product browser */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 md:mt-16">
        <div className="flex items-center gap-3 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hidden" data-testid="hero-strip">
          {SLIDES.map((s, i) => {
            const active = i === idx;
            const { Icon } = s;
            return (
              <button
                key={s.slug}
                onClick={() => setIdx(i)}
                className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-[5px] border text-[12.5px] transition-all ${
                  active
                    ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)] text-white"
                    : "border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/75 hover:border-[color:var(--gv-ink)]/30 bg-white"
                }`}
                data-testid={`hero-chip-${s.slug}`}
              >
                <Icon size={13} /> {s.eye}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Pill = ({ label, value }) => (
  <div className="bg-white/80 backdrop-blur border border-[color:var(--gv-line)] rounded-[5px] p-3">
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</div>
    <div className="text-[13px] text-[color:var(--gv-ink)]/85 leading-snug">{value}</div>
  </div>
);

// Small helper so both mockups can render the same footer chip (product
// name + slide index). Kept inline to avoid a fourth React component.
const c_eye_for = (slide, t) => t(slide.eyeKey || "hero.slide.default");

// iPhone-shaped mockup — dark bezel, dynamic island, home indicator.
// The screenshot is rendered with `object-contain` so nothing is cropped.
// Aspect ratio mimics an iPhone 15 (~9:19.5).
const PhoneMockup = ({ src, alt, testId, eye, idx, total }) => (
  <div className="mx-auto max-w-[280px] md:max-w-[320px]">
    <div className="relative rounded-[42px] bg-[#111827] p-2.5 shadow-[0_40px_80px_-30px_rgba(46,80,103,0.55)]">
      {/* Inner screen — light bg so screenshots on white don't look cut off */}
      <div className="relative rounded-[34px] overflow-hidden bg-white" style={{ aspectRatio: "9 / 19.5" }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain object-top"
          data-testid={testId}
        />
        {/* Dynamic island */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-[#111827]" />
      </div>
    </div>
    <div className="mt-4 flex items-center justify-between text-[11.5px] text-[color:var(--gv-ink)]/55 px-1">
      <span className="uppercase tracking-widest">{eye}</span>
      <span>{idx + 1} / {total}</span>
    </div>
  </div>
);

// macOS/Chrome-style browser window — 3 traffic-light dots on top,
// screenshot below in a 16:10 frame. `object-cover object-top` keeps
// the interesting part of the site visible without letterboxing.
const BrowserMockup = ({ src, alt, testId, eye, idx, total }) => (
  <div className="mx-auto max-w-[560px]">
    <div className="rounded-[10px] overflow-hidden border border-[color:var(--gv-line)] shadow-[0_40px_80px_-30px_rgba(46,80,103,0.45)] bg-white">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)]">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <div className="flex-1 flex justify-center">
          <div className="px-3 py-0.5 rounded-full bg-white border border-[color:var(--gv-line)] text-[10.5px] text-[color:var(--gv-ink)]/55 tabular-nums">
            geovinum.io
          </div>
        </div>
      </div>
      <div className="relative bg-white" style={{ aspectRatio: "16 / 10" }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
          data-testid={testId}
        />
      </div>
    </div>
    <div className="mt-4 flex items-center justify-between text-[11.5px] text-[color:var(--gv-ink)]/55 px-1">
      <span className="uppercase tracking-widest">{eye}</span>
      <span>{idx + 1} / {total}</span>
    </div>
  </div>
);

export default AnimatedHero;
